import { Injectable, inject } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { exhaustMap, map, switchMap } from 'rxjs/operators';
import { filterNil, selectRouteParams } from '../../store';
import { catchSwitchMapError } from '../../utils/catch-switch-map-error';
import { surveyAction } from '../survey/survey.actions';
import { surveyFeature } from '../survey/survey.selectors';
import { evaluateSurveyAction } from './evaluate-survey.actions';
import { EvaluateSurveyResources } from './evaluate-survey.resources';

@Injectable()
export class EvaluateSurveyEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private resources = inject(EvaluateSurveyResources);

  loadComputeEntrySurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(evaluateSurveyAction.loadEvaluateSurvey),
      concatLatestFrom(() => [
        this.store.pipe(select(selectRouteParams('reference'))),
      ]),
      switchMap(([, reference]) =>
        this.resources.getSurveyData(reference as string)
      ),
      map(({ surveyTemplate, surveyAnswer, responseId }) => {
        return surveyAction.loadSurveySuccess({
          surveyTemplate,
          surveyAnswer,
          responseId,
        });
      }),
      catchSwitchMapError((error: Error) => surveyAction.handleError({ error }))
    )
  );

  submitAnswer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(evaluateSurveyAction.submitAnswer),
      concatLatestFrom(() => [
        this.store.select(surveyFeature.selectSurveyAnswer).pipe(filterNil()),
      ]),
      exhaustMap(([, surveyAnswer]) =>
        this.resources.submit(surveyAnswer.ref, surveyAnswer)
      ),
      map((response) => evaluateSurveyAction.submitAnswerSuccess({ response })),
      catchSwitchMapError((error: Error) =>
        evaluateSurveyAction.submitAnswerError({ error })
      )
    )
  );

  restart = createEffect(
    () =>
      this.actions$.pipe(
        ofType(evaluateSurveyAction.restart),
        map(() => window.location.reload())
      ),
    { dispatch: false }
  );

  handleError = createEffect(() =>
    this.actions$.pipe(
      ofType(evaluateSurveyAction.submitAnswerError),
      map(({ error }) => surveyAction.handleError({ error }))
    )
  );
}
